import { executeCapability } from '../utils/capabilityExecute'
import type { EmployeeCert } from './profileService'
import { parseAdvanceCategoryDefs, type AdvanceCategoryDef } from '../utils/advanceCategories'

export type EeapRequestType = 'greenHand' | 'boots' | 'equipment'

export type AdvanceCategory = string

export const MAX_ADVANCE_IMAGES = 10

export type EeapHistoryRow = {
  id: string
  date: string | null
  amount: number
  employerContribution: number
  employeeAmount: number
  description: string
  category: AdvanceCategory
  categoryLabel?: string
  itemCategory?: string
  scheduled?: boolean
  establishedBalance?: number
  runningBalance?: number
  imageUrls?: string[]
}

export type EeapSummary = {
  annualAllowance: number
  remainingAllowance: number
  outstandingBalance: number
  outstandingAdvance: number
  outstandingEeapAdvance?: number
  outstandingTotal?: number
  outstandingByCategory?: Record<string, number>
  outstandingCap: number
  upcomingDeductions?: EeapHistoryRow[]
  hireDate: string | null
  windowResetsOn: string | null
  daysSinceHire: number
  tenureDays: { greenHand: number; boots: number; equipment: number }
  tenureEligible: { greenHand: boolean; boots: boolean; equipment: boolean }
  hrClear: boolean
  hasCurrentCerts: boolean
  certs: EmployeeCert[]
  employeeName: string
  pendingManagerReview: boolean
  formMapped: boolean
}

export type AdvanceTransaction = {
  id: string
  date: string | null
  category: AdvanceCategory
  categoryLabel?: string
  employeeId: string
  employeeName: string
  amount: number
  employerContribution: number
  employeeAmount: number
  description: string
  itemCategory?: string
  runningBalance?: number
  scheduled?: boolean
  establishedBalance?: number
  imageUrls?: string[]
}

export type AdvanceTransactionInput = {
  employeeId: string
  date: string
  category: AdvanceCategory
  amount: number
  employerContribution: number
  description: string
  itemCategory?: string
  establishedBalance?: number | null
  imageUrls?: string[]
}

export type EeapSubmitInput = {
  type: EeapRequestType
  totalRequestCost?: number
  exceptionAcknowledged: boolean
  purchaseMyself: boolean
  details?: {
    category?: string
    description?: string
    url?: string
  }
  signature: {
    dataUrl: string
    clientDate: string
    auth?: unknown
  }
}

class EeapService {
  async getSummary(): Promise<EeapSummary> {
    return executeCapability<EeapSummary>({
      app: 'profile',
      capability: 'eeap.getSummary',
    })
  }

  async listHistory(): Promise<EeapHistoryRow[]> {
    const data = await executeCapability<EeapHistoryRow[]>({
      app: 'profile',
      capability: 'eeap.listHistory',
    })
    return Array.isArray(data) ? data : []
  }

  async listPreapprovedCategories(): Promise<string[]> {
    const data = await executeCapability<{ categories?: string[] }>({
      app: 'profile',
      capability: 'eeap.listPreapprovedCategories',
    })
    return Array.isArray(data?.categories) ? data.categories.filter(Boolean) : []
  }

  async listAdvanceCategories(): Promise<AdvanceCategoryDef[]> {
    const data = await executeCapability<{ categories?: AdvanceCategoryDef[] }>({
      app: 'profile',
      capability: 'eeap.listAdvanceCategories',
    })
    return parseAdvanceCategoryDefs(data?.categories)
  }

  async submitRequest(input: EeapSubmitInput) {
    return executeCapability({
      app: 'profile',
      capability: 'eeap.submitRequest',
      input,
    })
  }

  async addTransaction(input: AdvanceTransactionInput): Promise<AdvanceTransaction> {
    return executeCapability<AdvanceTransaction>({
      app: 'profile',
      capability: 'eeap.addTransaction',
      input,
    })
  }

  async listTransactions(): Promise<{
    transactions: AdvanceTransaction[]
    hireDates: Record<string, string | null>
  }> {
    const data = await executeCapability<
      AdvanceTransaction[] | { transactions?: AdvanceTransaction[]; hireDates?: Record<string, string | null> }
    >({
      app: 'profile',
      capability: 'eeap.listTransactions',
    })
    if (Array.isArray(data)) return { transactions: data, hireDates: {} }
    return {
      transactions: Array.isArray(data?.transactions) ? data.transactions : [],
      hireDates: data?.hireDates && typeof data.hireDates === 'object' ? data.hireDates : {},
    }
  }

  async updateTransaction(input: AdvanceTransactionInput & { id: string }): Promise<AdvanceTransaction> {
    return executeCapability<AdvanceTransaction>({
      app: 'profile',
      capability: 'eeap.updateTransaction',
      input,
    })
  }

  async deleteTransaction(id: string, currentPassword: string): Promise<{ id: string; deleted: boolean }> {
    return executeCapability<{ id: string; deleted: boolean }>({
      app: 'profile',
      capability: 'eeap.deleteTransaction',
      input: { id, currentPassword },
    })
  }
}

export const eeapService = new EeapService()
